'use client';

import { useLogger } from 'next-axiom';
import { useTransition } from 'react';
import { useElevarEvents } from '../../elevar';
import { serializeCartItemToElevarProduct } from '../../elevar/elevar-helpers';
import {
  type KlaviyoCartItem,
  hasKlaviyo,
  trackAddedSampleToCartKlaviyo,
  trackAddedToCartKlaviyo,
} from '../../klaviyo/events';
import { useSearchDialogState } from '../../layout/search-dialog/use-search-dialog-state';
import { removeGid } from '../../lib';
import { usePreviousPage } from '../../lib/hooks';
import { addToCart } from '../actions';
import { useCart } from '../context';
import type { Product, ProductVariant } from '../types';
import { useClearCartCache } from './use-clear-cart-cache';
import { useShopifyAnalytics } from './use-shopify-analytics';

export function useAddToCart({ domain }: { domain: string }) {
  const { clearCartCache } = useClearCartCache();
  const {
    cart,
    addCartItem,
    open,
    setOpen,
    setLoading,
    activeItemQuantity: quantity,
    setActiveItemQuantity: setQuantity,
  } = useCart();
  const { sendAddToCart: sendAddToCartShopify } = useShopifyAnalytics();
  const [_, startTransition] = useTransition();
  const previousPage = usePreviousPage();
  const log = useLogger().with({
    location: 'useAddToCart',
  });
  const { trackAddToCart } = useElevarEvents();
  const { setIsOpen: setSearchDialogOpen } = useSearchDialogState();

  const addItem = ({
    product,
    variant,
    quantity,
    wastageMultiplier = 1,
    maxQuantityAvailable,
    productUrl,
  }: {
    product: Product;
    variant: ProductVariant;
    quantity: number;
    wastageMultiplier?: number;
    maxQuantityAvailable: number;
    productUrl?: string;
  }) => {
    setLoading(true);

    // Update local cart state with inventory validation
    const success = addCartItem({
      product,
      variant,
      quantity,
      squareMetersPerBox: product.squareMetersPerBox,
      wastageMultiplier,
      maxQuantityAvailable,
    });

    // Only proceed if validation passed
    if (!success) {
      setLoading(false);
      return;
    }

    // Only open cart if it's currently closed to avoid triggering tracking with old data
    if (!open) {
      setOpen(true);
      // Close search dialog when cart opens to avoid z-index conflicts
      setSearchDialogOpen(false);
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    startTransition(async () => {
      // ? Why not using addToCartAction?
      const shopifyCart = await addToCart(cart?.id ?? '', [
        {
          merchandiseId: variant.gid,
          quantity,
          attributes: productUrl
            ? [{ key: '_productUrl', value: productUrl }]
            : undefined,
        },
      ]);

      clearCartCache();

      if (!shopifyCart) {
        log.error('Error adding item to cart');
        return;
      }

      if ('error' in shopifyCart) {
        log.error('Error adding item to cart', { error: shopifyCart.error });
        return;
      }

      if (cart?.id) {
        sendAddToCartShopify({
          cartId: cart.id,
          totalValue: Number(shopifyCart.node.cost.totalAmount.amount),
          products: [
            {
              productGid: shopifyCart.node.merchandise.product.id,
              variantGid: shopifyCart.node.merchandise.id,
              name: shopifyCart.node.merchandise.product.title,
              brand: shopifyCart.node.merchandise.product.vendor,
              price: String(shopifyCart.node.merchandise.price.amount),
              quantity,
            },
          ],
        });

        trackAddToCart({
          list: previousPage?.collection ?? '',
          products: [
            serializeCartItemToElevarProduct({
              item: shopifyCart.node,
              domain,
              list: previousPage?.collection ?? '',
              quantity,
            }),
          ],
        });

        if (hasKlaviyo()) {
          const cartItem = shopifyCart.node;
          const klaviyoItem: KlaviyoCartItem = {
            ProductID: removeGid(cartItem.merchandise.product.id),
            ProductName: cartItem.merchandise.product.title,
            ProductURL: `${domain}/products/${cartItem.merchandise.product.handle}`,
            ImageURL: cartItem.merchandise.product.featuredImage?.url,
            Price: Number(cartItem.cost.subtotalAmount.amount),
            ProductCategories: cartItem.merchandise.product.category?.name
              ? [cartItem.merchandise.product.category.name]
              : undefined,
            Quantity: cartItem.quantity,
          };

          const isSample =
            cartItem.merchandise.product.productType === 'SAMPLE';

          if (isSample) {
            trackAddedSampleToCartKlaviyo({
              items: [klaviyoItem],
              totalValue: Number(shopifyCart.node.cost.totalAmount.amount),
            });
          } else {
            trackAddedToCartKlaviyo({
              items: [klaviyoItem],
              totalValue: Number(shopifyCart.node.cost.totalAmount.amount),
            });
          }
        }
      }
    });
    setQuantity(1);
    setLoading(false);
  };

  return {
    addItem,
    quantity,
    setQuantity,
  };
}
